import React from 'react';
import { Link } from 'react-router-dom';
import ImageToPDF from '../components/ImageToPDF';
import Seo from '../components/seo/Seo';
import { softwareAppSchema, howToSchema, breadcrumbSchema } from '../components/seo/jsonld';
import GoogleAd from '../components/GoogleAd';
import { AD_SLOTS } from '../components/adSlots';

const PATH = '/image-to-pdf';
const TITLE = 'Image to PDF Converter — JPG & PNG to PDF';
const DESCRIPTION = 'Convert JPG, JPEG, and PNG images to PDF in your browser. Fit to A4, preserve quality, no uploads, no watermarks. 100% free and private.';

const ImageToPdfPage = () => (
    <>
        <Seo
            title={TITLE}
            description={DESCRIPTION}
            path={PATH}
            jsonLd={[
                softwareAppSchema({ name: 'Image to PDF Converter', description: DESCRIPTION, path: PATH }),
                howToSchema({
                    name: 'How to convert an image to PDF',
                    description: 'Turn a JPG, JPEG, or PNG image into a print-ready PDF sized to A4.',
                    steps: [
                        'Pick a JPG, JPEG, or PNG file from your device.',
                        'Click "Convert to PDF".',
                        'The PDF downloads automatically with the same base filename.',
                    ],
                }),
                breadcrumbSchema([
                    { name: 'Home', path: '/' },
                    { name: 'Image to PDF', path: PATH },
                ]),
            ]}
        />

        <div className="page-container">
            <article>
                <header className="page-header">
                    <h1>Image to PDF Converter</h1>
                    <p className="page-lead">
                        Turn a JPG, JPEG, or PNG photo into a print-ready PDF — sized to A4,
                        centered, and ready to attach to an email or upload to a form.
                    </p>
                </header>

                <section className="tool-embed">
                    <ImageToPDF />
                </section>

                <GoogleAd slot={AD_SLOTS.IMAGE_TO_PDF} className="ad-slot-inline" />

                <section className="content-section">
                    <h2>How to convert an image to PDF</h2>
                    <ol>
                        <li>Pick a JPG, JPEG, or PNG file from your device.</li>
                        <li>Click <strong>Convert to PDF</strong>.</li>
                        <li>The PDF downloads automatically with the same base filename.</li>
                    </ol>

                    <h2>When you actually need a PDF instead of an image</h2>
                    <p>
                        Government portals, university applications, and corporate HR systems
                        almost always require PDFs. They reject image uploads partly because
                        images have no embedded metadata about page size or print orientation —
                        the file format gives no guarantee of how it will render when printed.
                        A PDF, on the other hand, specifies exact page geometry. This tool fits
                        your image to an A4 page with reasonable margins, which is what most
                        forms expect.
                    </p>
                    <p>
                        Common scenarios where you need an image-to-PDF conversion:
                    </p>
                    <ul>
                        <li>Scanned ID cards, driving licences, or passports submitted as proof of identity.</li>
                        <li>Photos of handwritten signatures for e-signing workflows.</li>
                        <li>Snapshots of receipts for expense reimbursement.</li>
                        <li>Photographed whiteboards from a meeting, bundled for a project archive.</li>
                        <li>Single-page artwork or sketches packaged for a portfolio.</li>
                    </ul>

                    <h2>Quality and file size</h2>
                    <p>
                        The tool embeds the image at its native resolution — it does not compress
                        or resample. That means your PDF's visual quality matches your source
                        image exactly. If the source is a 12&nbsp;MP photo, the PDF will be
                        noticeably larger than the image file itself (a PDF carries more
                        structural overhead than a raw JPG). If size is a concern, compress the
                        PDF afterwards with our <Link to="/compress-pdf">Compress PDF tool</Link>.
                    </p>

                    <h2>Supported formats</h2>
                    <ul>
                        <li><strong>JPG / JPEG:</strong> ideal for photographs.</li>
                        <li><strong>PNG:</strong> ideal for screenshots and graphics with text.</li>
                    </ul>
                    <p>
                        HEIC (iPhone photos), WebP, AVIF, and TIFF aren't currently supported.
                        On iPhone, you can change the camera's capture format to JPEG in
                        <em> Settings &rarr; Camera &rarr; Formats &rarr; Most Compatible</em>,
                        or export individual photos as JPEG before uploading.
                    </p>

                    <h2>Multiple pages from multiple images</h2>
                    <p>
                        This tool currently produces one PDF per image. To combine several images
                        into a single multi-page PDF, use a desktop tool like Preview on macOS
                        (select images, choose &quot;Print&quot;, then &quot;Save as PDF&quot;)
                        or a dedicated merging service. We're considering adding multi-image
                        support to this tool — if that would help you, please say so via the
                        <Link to="/contact"> contact form</Link>.
                    </p>

                    <h2>Privacy note</h2>
                    <p>
                        Images often contain EXIF metadata — GPS coordinates, device model,
                        capture time. Because the conversion happens locally, this metadata never
                        leaves your device during the process. However, it may still be embedded
                        in the resulting PDF. If you're sharing the PDF with strangers and the
                        image came from a phone, consider stripping metadata using a tool like
                        ExifTool before distribution.
                    </p>
                </section>

                <section className="content-section">
                    <h2>Related tools</h2>
                    <ul>
                        <li><Link to="/word-to-pdf">Word to PDF</Link> — for .docx files.</li>
                        <li><Link to="/compress-pdf">Compress PDF</Link> — reduce file size after conversion.</li>
                        <li><Link to="/pdf-to-image">PDF to Image</Link> — the reverse direction.</li>
                    </ul>
                </section>
            </article>
        </div>
    </>
);

export default ImageToPdfPage;
